import { DistributionChannel, TicketStatus, TicketType } from "@xs2event/js-lib";

/**
 * What a bookingorder can sensibly show once downloading is allowed.
 *
 * The portal used to show the "external distributor" message for every order that had no item
 * on the xs2event distribution channel. Items with a broken category (category_id = 0) come
 * back without a distribution channel at all, so those orders got that message even when their
 * tickets were sitting right there in download_items. Resolve on what we can actually show
 * instead, and only claim external distribution when the items really say so.
 */
export type DeliveryState = "items" | "external" | "unavailable";

const PHYSICAL_TICKET_TYPES = [TicketType.PAPERTICKET, TicketType.COLLECT];
const EXTERNAL_CHANNELS = [DistributionChannel.EXTERNAL, DistributionChannel.EXTERNAL_END_CLIENT];
const INCOMPLETE_GUESTDATA_STATUSES = ["waitingfordistributor", "waitingforendcustomer"];

/** download_items that point at something openable, in the order the API returned them. */
export const downloadableLinks = (item) =>
  (Array.isArray(item?.download_items) ? item.download_items : []).filter(
    (link) => link?.download_link || link?.external_activation_link
  );

/** The item itself can carry a link too, next to (or instead of) its download_items. */
export const hasTicketLink = (item) =>
  !!item?.download_link || !!item?.external_activation_link || downloadableLinks(item).length > 0;

export const isPhysicalTicket = (item) => !!item?.type_ticket && PHYSICAL_TICKET_TYPES.includes(item.type_ticket);

export const isReturnedOrDeleted = (item) => item?.ticket_status === TicketStatus.RETURNED || !!item?.deleted;

const isOwnChannel = (item) => item?.distribution_channel === DistributionChannel.XS2EVENT;

const isExternalChannel = (item) => EXTERNAL_CHANNELS.includes(item?.distribution_channel);

export const hasPhysicalTickets = (items) => (Array.isArray(items) ? items : []).some(isPhysicalTicket);

export const hasOwnDistributionChannel = (items) => (Array.isArray(items) ? items : []).some(isOwnChannel);

export const hasIncompleteGuestdata = (bookingOrder) =>
  INCOMPLETE_GUESTDATA_STATUSES.includes(bookingOrder?.guestdata_status);

export const resolveDeliveryState = (bookingOrder): DeliveryState => {
  const items = Array.isArray(bookingOrder?.items) ? bookingOrder.items : [];

  if (!items.length) return "unavailable";

  // Guest data we are still waiting for is the one thing the customer can act on, and it is why
  // there are no tickets yet - always let them fill it in.
  if (hasIncompleteGuestdata(bookingOrder)) return "items";

  if (items.some(isOwnChannel)) return "items";

  // A link beats any channel bookkeeping: if we can hand over a ticket, hand it over. The
  // whole-order zip counts as one, even when the items themselves carry no links.
  if (items.some(hasTicketLink) || bookingOrder?.zip_sha) return "items";

  if (items.some(isExternalChannel)) return "external";

  // No channel, no links - but a paper/collect ticket or a returned ticket still has a story.
  if (items.some(isPhysicalTicket)) return "items";
  if (items.every(isReturnedOrDeleted)) return "items";

  return "unavailable";
};
